import React from 'react';
import { cn } from '../../lib/utils';
import { useTheme } from '../../contexts/ThemeContext';

export interface TablePaginationBarProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange?: (itemsPerPage: number) => void;
  pageSizeOptions?: number[];
  /** Short label before page-size select (default: "Rows") */
  pageSizeLabel?: string;
  className?: string;
  /** Extra content after the range summary (e.g. select-all link) */
  summaryExtra?: React.ReactNode;
  /** Hide page controls when there is only one page (summary + page size still shown) */
  hideNavWhenSinglePage?: boolean;
}

function getRangeStart(currentPage: number, itemsPerPage: number): number {
  return currentPage === 1 ? 1 : (currentPage - 1) * itemsPerPage + 1;
}

function getRangeEnd(currentPage: number, itemsPerPage: number, totalItems: number): number {
  return Math.min(currentPage * itemsPerPage, totalItems);
}

function getVisiblePages(currentPage: number, totalPages: number): number[] {
  return Array.from({ length: totalPages }, (_, i) => i + 1).filter(
    (page) =>
      page === 1 ||
      page === totalPages ||
      (page >= currentPage - 1 && page <= currentPage + 1)
  );
}

const ChevronLeft = () => (
  <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
    <path
      fillRule="evenodd"
      d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
      clipRule="evenodd"
    />
  </svg>
);

const ChevronRight = () => (
  <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
    <path
      fillRule="evenodd"
      d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
      clipRule="evenodd"
    />
  </svg>
);

export function TablePaginationBar({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
  pageSizeOptions = [50, 100, 200, 500],
  pageSizeLabel = 'Rows',
  className,
  summaryExtra,
  hideNavWhenSinglePage = false,
}: TablePaginationBarProps) {
  const { colors } = useTheme();
  const rangeStart = getRangeStart(currentPage, itemsPerPage);
  const rangeEnd = getRangeEnd(currentPage, itemsPerPage, totalItems);
  const showNav = totalPages > 1 || !hideNavWhenSinglePage;
  const visiblePages = getVisiblePages(currentPage, totalPages);

  const navBtn = cn(
    'inline-flex h-7 w-7 items-center justify-center transition-colors disabled:opacity-40 disabled:cursor-not-allowed',
    colors.textMuted,
    colors.interactiveHover
  );

  const pageBtn = (active: boolean) =>
    cn(
      'inline-flex h-7 min-w-[1.75rem] items-center justify-center px-1.5 text-xs tabular-nums transition-colors',
      active
        ? cn('font-semibold', colors.primaryFg, colors.primaryBorderAll)
        : cn(colors.textMuted, colors.interactiveHover)
    );

  return (
    <div
      className={cn(
        'flex shrink-0 items-center gap-2 border-t px-3 py-1.5 sm:px-4',
        colors.surface,
        colors.border,
        className
      )}
    >
      {/* Mobile: prev / page indicator / next */}
      <div className="flex w-full items-center justify-between gap-2 sm:hidden">
        <button
          type="button"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={navBtn}
          aria-label="Previous page"
        >
          <ChevronLeft />
        </button>
        <span className={cn('text-xs tabular-nums whitespace-nowrap', colors.textMuted)}>
          {rangeStart}–{rangeEnd} of {totalItems.toLocaleString()}
          {totalPages > 1 && (
            <span className="mx-1 opacity-50">·</span>
          )}
          {totalPages > 1 && `${currentPage}/${totalPages}`}
        </span>
        <button
          type="button"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={navBtn}
          aria-label="Next page"
        >
          <ChevronRight />
        </button>
      </div>

      {/* Desktop: single compact row */}
      <div className="hidden w-full items-center gap-3 sm:flex">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <span className={cn('text-xs tabular-nums whitespace-nowrap', colors.textMuted)}>
            <span className={cn('font-medium', colors.textSecondary)}>
              {rangeStart.toLocaleString()}–{rangeEnd.toLocaleString()}
            </span>
            {' of '}
            <span className={cn('font-medium', colors.textSecondary)}>{totalItems.toLocaleString()}</span>
          </span>
          {summaryExtra}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {onItemsPerPageChange && (
            <label className={cn('inline-flex items-center gap-1 text-xs whitespace-nowrap', colors.textMuted)}>
              <span>{pageSizeLabel}</span>
              <select
                value={itemsPerPage}
                onChange={(e) => onItemsPerPageChange(parseInt(e.target.value, 10))}
                className={cn(
                  'h-7 rounded border py-0 pl-1.5 pr-6 text-xs leading-none',
                  colors.border,
                  colors.surface,
                  colors.text,
                  'focus:outline-none focus:ring-1 focus:ring-indigo-500'
                )}
                aria-label={`${pageSizeLabel} per page`}
              >
                {pageSizeOptions.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </label>
          )}

          {showNav && totalPages > 1 && (
            <nav
              className={cn('inline-flex h-7 items-stretch overflow-hidden rounded-md border', colors.border)}
              aria-label="Pagination"
            >
              <button
                type="button"
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={cn(navBtn, 'border-r', colors.border)}
                aria-label="Previous page"
              >
                <ChevronLeft />
              </button>
              {visiblePages.map((page, index, array) => {
                const showEllipsis = index > 0 && page - array[index - 1] > 1;
                return (
                  <React.Fragment key={page}>
                    {showEllipsis && (
                      <span
                        className={cn(
                          'inline-flex h-7 min-w-[1.75rem] items-center justify-center border-r px-1 text-xs',
                          colors.border,
                          colors.textMuted
                        )}
                        aria-hidden
                      >
                        …
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => onPageChange(page)}
                      className={cn(
                        pageBtn(page === currentPage),
                        index < array.length - 1 || currentPage < totalPages ? cn('border-r', colors.border) : ''
                      )}
                      aria-current={page === currentPage ? 'page' : undefined}
                    >
                      {page}
                    </button>
                  </React.Fragment>
                );
              })}
              <button
                type="button"
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={navBtn}
                aria-label="Next page"
              >
                <ChevronRight />
              </button>
            </nav>
          )}
        </div>
      </div>
    </div>
  );
}

export default TablePaginationBar;
